import React, { useState, useEffect } from 'react';
import { occupiedPositionsInRange, filledPositions } from '../../utils/deliveryOccupancy';
import { addDays, dateKey, todayKey } from '../../utils/date';
import { deliveryRunAPI, orderAPI } from '../../services/api';
import TruckGraphic from './TruckGraphic';
import SearchOrderModal from './SearchOrderModal';
import SplitOrderItemModal from './SplitOrderItemModal';
import CreateRunModal from './CreateRunModal';
import RouteMapView from './RouteMapView';
import './Planning.css';

/**
 * The date a new delivery run should default to.
 *
 * Tomorrow, unless the planner is looking at a different day - then it is
 * the day they are looking at. A run is planned for the day it will be
 * delivered, and the planner is nearly always looking at today.
 */
const newRunDateFor = (selectedDate) => {
  const today = todayKey();
  return selectedDate === today ? addDays(today, 1) : selectedDate;
};

function PlanningPage({ user }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [deliveryRuns, setDeliveryRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [scheduledOrders, setScheduledOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [addingDelivery, setAddingDelivery] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showFullscreenMap, setShowFullscreenMap] = useState(false);
  const [wigglePosition, setWigglePosition] = useState(null);
  const [splitModalData, setSplitModalData] = useState({ isOpen: false, order: null, line: null });

  // Load today's date as default, or use URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dateParam = urlParams.get('date');
    const runParam = urlParams.get('run');

    if (dateParam) {
      setSelectedDate(dateParam);
    } else {
      setSelectedDate(todayKey());
    }

    // If a run ID is provided in URL, load it
    if (runParam) {
      loadRunFromUrl(runParam);
    }
  }, []);

  // Load a specific run from URL parameter
  const loadRunFromUrl = async (runId) => {
    try {
      setLoading(true);
      setError(null);
      const run = await deliveryRunAPI.getById(runId);
      // Set the date to match the delivery run's date
      // delivery_date arrives as a full ISO datetime; take the date part.
      const runDate = dateKey(run.delivery_date) || todayKey();
      setSelectedDate(runDate);
      setSelectedRun(run);
      setShowTrailerVisible(run.number_of_positions !== 7);
      // Also fetch delivery runs for this date to populate the sidebar dropdown
      const runs = await deliveryRunAPI.getByDate(runDate);
      setDeliveryRuns(runs);
    } catch (err) {
      console.error('Error fetching delivery run from URL:', err);
      setError('Failed to load delivery run from URL');
    } finally {
      setLoading(false);
    }
  };

  // Fetch delivery runs when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchDeliveryRuns();
      fetchScheduledOrders();
    }
  }, [selectedDate]);

  // Fetch delivery runs for selected date
  const fetchDeliveryRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const runs = await deliveryRunAPI.getByDate(selectedDate);
      setDeliveryRuns(runs);
      // Preserve selected run with full details (including deliveries) if it still exists
      if (selectedRun) {
        const runExists = runs.find(r => r.id === selectedRun.id);
        if (runExists) {
          const fullRun = await deliveryRunAPI.getById(selectedRun.id);
          setSelectedRun(fullRun);
        } else {
          setSelectedRun(null);
        }
      }
    } catch (err) {
      console.error('Error fetching delivery runs:', err);
      setError('Failed to load delivery runs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders with schedule_date matching selected date
  const fetchScheduledOrders = async () => {
    setOrdersLoading(true);
    try {
      const orders = await orderAPI.getUnallocatedByScheduleDate(selectedDate);
      setScheduledOrders(orders);
    } catch (err) {
      console.error('Error fetching scheduled orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Handle date selection change
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedRun(null);
  };

  // Format start time for display
  const formatStartTime = (timeString, is24Hour = false) => {
    if (!timeString) return '';
    // Use regex to extract hours and minutes from various datetime formats
    const match = String(timeString).match(/(\d{2}):(\d{2})/);
    if (!match) return timeString; // Fallback if format is unexpected
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (is24Hour) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Calculate ETA range for a stop at a given cumulative minutes from start
  // Returns formatted string like "9:00 AM - 10:00 AM" with earliest and latest (+1hr delay)
  const calculateETARange = (startTime, cumulativeMinutes) => {
    if (!startTime || cumulativeMinutes < 0) return null;
    
    // Earliest: just travel time + 15 min unload
    const earliestMinutes = cumulativeMinutes;
    const earliest = addMinutesToTime(startTime, earliestMinutes);
    
    // Latest: add 1 hour buffer for delays
    const latestMinutes = cumulativeMinutes + 60;
    const latest = addMinutesToTime(startTime, latestMinutes);
    
    return `${formatStartTime(earliest)} - ${formatStartTime(latest)}`;
  };

  // Add minutes to time string (HH:MM format)
  const addMinutesToTime = (timeStr, minutesToAdd) => {
    if (!timeStr) return null;
    const match = String(timeStr).match(/(\d{2}):(\d{2})/);
    if (!match) return null;
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const totalMinutes = hours * 60 + minutes + minutesToAdd;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  };

  // Handle run selection
  const handleRunSelect = async (e) => {
    const runId = e.target.value;
    if (runId === '') {
      setSelectedRun(null);
    } else {
      try {
        const run = await deliveryRunAPI.getById(runId);
        setSelectedRun(run);
        // Initialize trailer visibility based on number_of_positions
        setShowTrailerVisible(run.number_of_positions !== 7);
      } catch (err) {
        console.error('Error fetching delivery run:', err);
        setError('Failed to load delivery run details');
      }
    }
  };

  // Handle previous run navigation
  const handlePreviousRun = () => {
    if (deliveryRuns.length === 0) return;
    const currentIndex = selectedRun 
      ? deliveryRuns.findIndex(r => r.id === selectedRun.id)
      : -1;
    const prevIndex = currentIndex <= 0 ? deliveryRuns.length - 1 : currentIndex - 1;
    const prevRun = deliveryRuns[prevIndex];
    if (prevRun) {
      setSelectedRun(prevRun);
      setShowTrailerVisible(prevRun.number_of_positions !== 7);
    }
  };

  // Handle next run navigation
  const handleNextRun = () => {
    if (deliveryRuns.length === 0) return;
    const currentIndex = selectedRun 
      ? deliveryRuns.findIndex(r => r.id === selectedRun.id)
      : -1;
    const nextIndex = currentIndex >= deliveryRuns.length - 1 ? 0 : currentIndex + 1;
    const nextRun = deliveryRuns[nextIndex];
    if (nextRun) {
      setSelectedRun(nextRun);
      setShowTrailerVisible(nextRun.number_of_positions !== 7);
    }
  };

  // The create-run dialog is shared with the Delivery Diary. It owns its own
  // form state, asks the server about vehicle availability and previews the
  // code the run will be given.
  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  // A run that was just created is selected straight away, so the planner
  // lands on the new run rather than back on the day they started from.
  const handleRunCreated = async (run) => {
    await fetchDeliveryRuns();

    if (run) {
      setSelectedRun(run);
    }
  };

  // Format weight code for display
  const formatWeightCode = (weightCode) => {
    const code = String(weightCode);
    switch (code) {
      case '1':
        return 'Light';
      case '2':
        return 'Medium';
      case '3':
        return 'Heavy';
      default:
        return weightCode || '??';
    }
  };

  // Get status label for display
  const getStatusLabel = (status) => {
    switch (status) {
      case 1:
        return 'Planning';
      case 2:
        return 'Confirmed';
      case 3:
        return 'Live';
      case 4:
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  // Get status class for badge styling
  const getStatusClass = (status) => {
    switch (status) {
      case 1:
        return 'status-planning';
      case 2:
        return 'status-confirmed';
      case 3:
        return 'status-live';
      case 4:
        return 'status-completed';
      default:
        return 'status-unknown';
    }
  };

  // Handle drag start for order items
  const handleDragStart = (e, order, line) => {
    const orderData = {
      orderId: order.id,
      customerId: order.customer?.id,
      customerName: order.customer?.fullname,
      deliveryAddress: order.delivery_address,
      lineId: line.id,
      productCode: line.product?.code || 'Unknown',
      qty: line.qty,
      size: line.size || 1,
    };
    e.dataTransfer.setData('application/json', JSON.stringify(orderData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle open split modal
  const handleOpenSplitModal = (order, line) => {
    setSplitModalData({ isOpen: true, order, line });
  };

  // Handle close split modal
  const handleCloseSplitModal = () => {
    setSplitModalData({ isOpen: false, order: null, line: null });
  };

  // Handle split complete - refresh orders
  const handleSplitComplete = () => {
    fetchScheduledOrders();
  };

  // Handle drop on truck
  const handleDropOrder = async (orderData, position) => {
    if (!selectedRun) {
      alert('Please select a delivery run first');
      return;
    }

    if (addingDelivery) {
      return;
    }

    setAddingDelivery(true);

    try {
      await deliveryRunAPI.addDelivery(selectedRun.id, {
        customer_order_id: orderData.orderId,
        customer_id: orderData.customerId,
        loading_position: position,
        customer_order_line_id: orderData.lineId,
      });

      // Refresh the run data to show the new delivery
      const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
      setSelectedRun(updatedRun);
      
      // Also refresh the scheduled orders to show the order was allocated
      await fetchScheduledOrders();
    } catch (err) {
      console.error('Error adding delivery:', err);
      alert(err.response?.data?.message || 'Failed to add delivery to run');
    } finally {
      setAddingDelivery(false);
    }
  };

  // Handle remove delivery from truck
  const handleRemoveDelivery = async (deliveryId, position, deliveryItemId = null) => {
    if (!selectedRun) return;
    
    const message = deliveryItemId 
      ? 'Remove this item from the run?' 
      : 'Remove this delivery from the run?';
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      await deliveryRunAPI.removeDelivery(selectedRun.id, deliveryId, deliveryItemId);
      
      // Refresh the run data
      const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
      setSelectedRun(updatedRun);
      
      // Also refresh the scheduled orders
      await fetchScheduledOrders();
    } catch (err) {
      console.error('Error removing delivery:', err);
      alert(err.response?.data?.message || 'Failed to remove delivery');
    }
  };

  // Handle reorder delivery within truck
  const handleReorderDelivery = async (deliveryId, newPosition, deliveryItemId) => {
    if (!selectedRun) return;

    try {
      await deliveryRunAPI.updateDeliveryPosition(selectedRun.id, deliveryId, newPosition, deliveryItemId);
      
      // Refresh the run data
      const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
      setSelectedRun(updatedRun);
    } catch (err) {
      console.error('Error reordering delivery:', err);
      alert(err.response?.data?.message || 'Failed to reorder delivery');
    }
  };

  // Handle confirm run (change status from Planning to Confirmed)
  const handleConfirmRun = async () => {
    if (!selectedRun) return;
    
    if (!window.confirm('Confirm this delivery run? This will change the status to Confirmed.')) {
      return;
    }

    try {
      // Prepare ETA data for each delivery
      const deliveriesWithEta = [];
      
      if (selectedRun.deliveries && routeLegs.length > 0) {
        // Validate route legs have meaningful durations (not all zero)
        const hasValidRouteData = routeLegs.some(leg => parseInt(leg.duration, 10) > 0);
        
        if (!hasValidRouteData) {
          console.warn('Route legs have no valid durations, ETAs will not be saved');
        }
        
        // Group deliveries by customer_order_id to get sorted order
        const orderGroups = {};
        selectedRun.deliveries.forEach((delivery) => {
          const orderId = delivery.customer_order_id;
          if (!orderGroups[orderId]) {
            orderGroups[orderId] = {
              deliveryId: delivery.id,
              items: [],
              maxPosition: 0
            };
          }
          // Add all items from this delivery to the group
          if (delivery.items) {
            delivery.items.forEach((item) => {
              const position = item.loading_position || 0;
              orderGroups[orderId].items.push(item);
              if (position > orderGroups[orderId].maxPosition) {
                orderGroups[orderId].maxPosition = position;
              }
            });
          }
        });
        
        // Sort by maxPosition descending (highest position first = last to deliver)
        const sortedGroups = Object.values(orderGroups).sort((a, b) => b.maxPosition - a.maxPosition);
        
        console.log('ETA Calculation:', {
          routeLegsCount: routeLegs.length,
          routeLegs: JSON.parse(JSON.stringify(routeLegs)),
          deliveryCount: sortedGroups.length,
          deliveries: sortedGroups.map(g => ({ id: g.deliveryId, maxPosition: g.maxPosition }))
        });
        
        // Calculate ETA for each order
        let cumulativeMinutes = 0;
        sortedGroups.forEach((group, seqIdx) => {
          // Add travel time for this leg (routeLegs[seqIdx] contains travel TO this stop)
          const legDurationMinutes = parseInt(routeLegs[seqIdx]?.duration || 0, 10);
          cumulativeMinutes += legDurationMinutes;
          // Add 15 min unload time for this stop
          cumulativeMinutes += 15;
          
          const earliest = addMinutesToTime(selectedRun.start_time, cumulativeMinutes);
          const latest = addMinutesToTime(selectedRun.start_time, cumulativeMinutes + 60);
          
          console.log(`Stop ${seqIdx + 1}: delivery ${group.deliveryId}, cumulative ${cumulativeMinutes}min (leg=${legDurationMinutes}), eta ${earliest}-${latest}`);
          
          deliveriesWithEta.push({
            delivery_id: group.deliveryId,
            eta_start: earliest,
            eta_end: latest
          });
        });
      } else {
        console.log('No ETA calculation - deliveries:', selectedRun?.deliveries?.length, 'routeLegs:', routeLegs.length);
      }
      
      console.log('Sending ETA update:', deliveriesWithEta);
      
      await deliveryRunAPI.update(selectedRun.id, { 
        status: 2,
        deliveries_with_eta: deliveriesWithEta
      });
      
      // Refresh the run data
      const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
      setSelectedRun(updatedRun);
      
      // Also refresh the runs list
      await fetchDeliveryRuns();
    } catch (err) {
      console.error('Error confirming run:', err);
      alert(err.response?.data?.message || 'Failed to confirm run');
    }
  };

  // Handle return to planning (change status from Confirmed back to Planning)
  const handleReturnToPlanning = async () => {
    if (!selectedRun) return;
    
    if (!window.confirm('Return this delivery run to Planning status?')) {
      return;
    }

    try {
      await deliveryRunAPI.update(selectedRun.id, { status: 1 });
      
      // Refresh the run data
      const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
      setSelectedRun(updatedRun);
      
      // Also refresh the runs list
      await fetchDeliveryRuns();
    } catch (err) {
      console.error('Error returning run to planning:', err);
      alert(err.response?.data?.message || 'Failed to return run to planning');
    }
  };

  // Handle hide trailer - removes deliveries from trailer positions
  const handleHideTrailer = async () => {
    if (!selectedRun) return;
    
    // Find deliveries in trailer positions (8-13)
    const trailerDeliveries = selectedRun.deliveries?.filter(d => {
      // A delivery is in the trailer if any of its items are in positions 8-13
      return d.items?.some(item => item.loading_position >= 8);
    });

    if (trailerDeliveries.length === 0) {
      // No trailer deliveries, just hide the trailer and update number_of_positions
      try {
        await deliveryRunAPI.update(selectedRun.id, { number_of_positions: 7 });
        const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
        setSelectedRun(updatedRun);
        setShowTrailerVisible(false);
      } catch (err) {
        console.error('Error updating number_of_positions:', err);
      }
      return;
    }

    if (!window.confirm(`Hiding the trailer will remove ${trailerDeliveries.length} delivery(ies) from trailer positions. Continue?`)) {
      return;
    }

    try {
      // Remove each delivery from the trailer
      for (const delivery of trailerDeliveries) {
        await deliveryRunAPI.removeDelivery(selectedRun.id, delivery.id);
      }
      
      // Update number_of_positions to 7 (truck only)
      await deliveryRunAPI.update(selectedRun.id, { number_of_positions: 7 });
      
      // Refresh the run data
      const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
      setSelectedRun(updatedRun);
      
      // Hide the trailer visually
      setShowTrailerVisible(false);
      
      // Also refresh the scheduled orders (pending allocation)
      await fetchScheduledOrders();
    } catch (err) {
      console.error('Error removing trailer deliveries:', err);
      alert(err.response?.data?.message || 'Failed to remove trailer deliveries');
    }
  };

  // Handle toggle trailer (show trailer again when truck-only)
  const handleToggleTrailer = async (show) => {
    if (!selectedRun) return;
    
    try {
      // Update number_of_positions to 13 (truck and trailer) if showing trailer
      if (show) {
        await deliveryRunAPI.update(selectedRun.id, { number_of_positions: 13 });
      }
      setShowTrailerVisible(show);
      
      // Refresh the run data
      const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
      setSelectedRun(updatedRun);
    } catch (err) {
      console.error('Error toggling trailer:', err);
      alert(err.response?.data?.message || 'Failed to toggle trailer');
    }
  };

  // State to control trailer visibility
  const [showTrailerVisible, setShowTrailerVisible] = useState(true);
  
  // State for editing a run
  const [showEditRunModal, setShowEditRunModal] = useState(false);
  const [editRunName, setEditRunName] = useState('');
  const [editRunStartTime, setEditRunStartTime] = useState('');
  const [editRunError, setEditRunError] = useState(null);
  const [savingRunEdit, setSavingRunEdit] = useState(false);
  const [estimatedFinishTime, setEstimatedFinishTime] = useState(null);
  const [routeLegs, setRouteLegs] = useState([]);

  // Open the edit modal for a run
  const openEditRunModal = (run) => {
    setEditRunName(run.name || '');
    setEditRunStartTime(formatStartTime(run.start_time, true));
    setEditRunError(null);
    setShowEditRunModal(true);
  };

  // Cancel editing
  const closeEditRunModal = () => {
    setShowEditRunModal(false);
    setEditRunError(null);
  };

  // Save the name and start time
  const handleSaveRunEdit = async (e) => {
    e.preventDefault();
    if (!selectedRun) return;

    setSavingRunEdit(true);
    setEditRunError(null);

    try {
      await deliveryRunAPI.update(selectedRun.id, {
        name: editRunName.trim() || null,
        start_time: editRunStartTime,
      });

      // Refresh the run data
      const updatedRun = await deliveryRunAPI.getById(selectedRun.id);
      setSelectedRun(updatedRun);

      // Also refresh the runs list
      await fetchDeliveryRuns();

      setShowEditRunModal(false);
    } catch (err) {
      console.error('Error updating run:', err);
      setEditRunError(err.response?.data?.message || 'Failed to update the run');
    } finally {
      setSavingRunEdit(false);
    }
  };

  return (
    <div className="scheduling-page">
      <div className="scheduling-wrapper">
        <div className="page-header-row">
          <div className="page-header">
            <h1>Deliveries</h1>
            <p>Manage delivery runs</p>
          </div>
          <div className="page-header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => window.location.href = `/deliveries/runsheet?date=${selectedDate}`}
              title="Open the runsheet for this date"
            >
              Runsheet
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowSearchModal(true)}
              title="Re-schedule orders onto a run"
            >
              🔍 Re-schedule
            </button>
          </div>
        </div>

        <div className="scheduling-container">
          {/* Left sidebar - 4/12 column */}
          <div className="scheduling-sidebar">
            <div className="tool-bar">
              <div className="form-group date-nav-group">
                <label htmlFor="date-picker">Select Date</label>
                <div className="date-nav-controls">
                  <button
                    type="button"
                    className="btn btn-date-nav"
                    onClick={() => {
                      setSelectedDate(addDays(selectedDate, -1));
                    }}
                    title="Previous day"
                  >
                    &lt;
                  </button>
                  <input
                    type="date"
                    id="date-picker"
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="form-control"
                  />
                  <button
                    type="button"
                    className="btn btn-date-nav"
                    onClick={() => {
                      setSelectedDate(addDays(selectedDate, 1));
                    }}
                    title="Next day"
                  >
                    &gt;
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="run-select">Delivery Run</label>
                <div className="run-select-row">
                  <button
                    type="button"
                    className="btn btn-nav-run"
                    onClick={handlePreviousRun}
                    disabled={deliveryRuns.length === 0}
                    title="Previous run"
                  >
                    &lt; 
                  </button>
                  <select
                    id="run-select"
                    value={selectedRun?.id || ''}
                    onChange={handleRunSelect}
                    className="form-control"
                    disabled={loading}
                  >
                    <option value="">-- Select a run --</option>
                    {deliveryRuns.map((run) => (
                      <option key={run.id} value={run.id}>
                        {run.name ? `${run.name} - ` : ''}{run.code || 'Unnamed'} - {formatStartTime(run.start_time)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-nav-run"
                    onClick={handleNextRun}
                    disabled={deliveryRuns.length === 0}
                    title="Next run"
                  >
                    &gt;
                  </button>
                </div>
                {deliveryRuns.length > 0 && (
                  <div className="run-status-summary">
                    {(() => {
                      const statusCounts = {};
                      deliveryRuns.forEach(run => {
                        const status = run.status;
                        statusCounts[status] = (statusCounts[status] || 0) + 1;
                      });
                      const parts = [];
                      if (statusCounts[1]) parts.push(`${statusCounts[1]} × Planning`);
                      if (statusCounts[2]) parts.push(`${statusCounts[2]} × Confirmed`);
                      if (statusCounts[3]) parts.push(`${statusCounts[3]} × Live`);
                      if (statusCounts[4]) parts.push(`${statusCounts[4]} × Completed`);
                      return parts.join(', ');
                    })()}
                  </div>
                )}
              </div>
              <div className="tool-bar-actions">
                <button className="btn btn-primary" onClick={openCreateModal}>
                  Create New Run
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={!selectedRun}
                  onClick={() => selectedRun && openEditRunModal(selectedRun)}
                  title={selectedRun
                    ? `Edit ${selectedRun.name || selectedRun.code}`
                    : 'Select a delivery run first'}
                >
                  Edit Run
                </button>
              </div>
            </div>

            {/* Pending Allocation Panel */}
            <div className="pending-allocation">
              <h3>Pending Allocation</h3>
              {scheduledOrders.length > 0 && (() => {
                // Calculate total cubes from all scheduled orders
                let totalCubes = 0;
                scheduledOrders.forEach((order) => {
                  if (order.order_lines && order.order_lines.length > 0) {
                    order.order_lines.forEach((line) => {
                      totalCubes += (line.size || 1) * (line.qty || 1);
                    });
                  }
                });
                return (
                  <p className="hint">
                    {scheduledOrders.length} orders, {totalCubes} cubes total
                  </p>
                );
              })()}
              {selectedRun && (() => {
                // Calculate available capacity in truck and trailer sections.
                //
                // This counter and the one below used to compute occupancy
                // separately and gave two different answers. Both now use
                // the shared helper, and the API's own total_positions /
                // filled_positions are used where the run provides them.
                const numberOfPositions = selectedRun?.total_positions
                  || selectedRun?.number_of_positions
                  || 13;
                const maxTruckPositions = 7;
                const maxTrailerPositions = numberOfPositions > 7 ? numberOfPositions - 7 : 0;

                // Every position occupied, counting every item of every
                // delivery. Only positions within the run count - legacy
                // data can hold a position beyond the run, and counting it
                // made this say fewer free positions than the API said
                // were filled.
                const occupied = occupiedPositionsInRange(
                    selectedRun?.deliveries,
                    numberOfPositions,
                );

                const truckOccupied = [...occupied].filter(p => p <= 7).length;
                const trailerOccupied = numberOfPositions > 7
                  ? [...occupied].filter(p => p >= 8).length
                  : 0;
                
                const availableTruck = showTrailerVisible ? (maxTruckPositions - truckOccupied) : (numberOfPositions - truckOccupied);
                const availableTrailer = showTrailerVisible ? (maxTrailerPositions - trailerOccupied) : 0;
                const totalAvailable = showTrailerVisible
                  ? (maxTruckPositions + maxTrailerPositions - [...occupied].length)
                  : (numberOfPositions - [...occupied].length);
                const filled = filledPositions(selectedRun?.deliveries, numberOfPositions);

                return (
                  <p className="hint">
                    {filled} of {numberOfPositions} positions filled
                    {` - ${totalAvailable} available`}
                    {' '}({availableTruck} truck
                    {showTrailerVisible && `, ${availableTrailer} trailer`})
                  </p>
                );
              })()}
              {ordersLoading ? (
                <div className="loading-small">
                  <div className="spinner-small"></div>
                </div>
              ) : scheduledOrders.length === 0 ? (
                <div className="empty-orders">
                  <p>No orders scheduled</p>
                </div>
              ) : (
                <div className="orders-list">
                  {(() => {
                    // Calculate max available space for any single item.
                    // Uses the same shared helper as the counter above, so
                    // the two agree - they used to compute occupancy
                    // separately and give different answers.
                    const numberOfPositions = selectedRun?.total_positions
                      || selectedRun?.number_of_positions
                      || 13;
                    const maxTruckPositions = 7;
                    const maxTrailerPositions = numberOfPositions > 7 ? numberOfPositions - 7 : 0;
                    const totalPositions = showTrailerVisible ? (maxTruckPositions + maxTrailerPositions) : numberOfPositions;

                    // Every position occupied, counting every item of every
                    // delivery, and only the positions within the run.
                    const occupied = occupiedPositionsInRange(
                        selectedRun?.deliveries,
                        numberOfPositions,
                    );

                    const availablePositions = totalPositions - occupied.size;
                    
                    // Filter order lines that can fit
                    const visibleOrders = [];
                    const hiddenCount = { count: 0 };
                    
                    scheduledOrders.forEach((order) => {
                      const orderHasDelivery = selectedRun?.deliveries?.some(
                        (d) => d.customer_order_id === order.id
                      );
                      
                      if (order.order_lines && order.order_lines.length > 0) {
                        order.order_lines.forEach((line, idx) => {

                          const lineSize = (line.size) * (line.qty || 1);
                          // Hide items with 0 or negative size, or items too large for remaining space
                          if (lineSize <= 0) {
                            //hiddenCount.count++;

                          console.log('Evaluating order line for visibility:', {
                            orderId: order.id,
                            lineId: line.id,
                            product: line.product?.code,
                            size: line.size,
                            qty: line.qty,
                            lineSize: (line.size) * (line.qty || 1),
                            availablePositions,
                            occupiedCount: occupied.size
                          });
                          
                            return;
                          }
                          // Only hide if:
                          // 1. There are occupied positions (truck is not empty)
                          // 2. AND item is larger than ALL available space
                          const isTruckEmpty = occupied.size === 0;
                          const canFit = isTruckEmpty || lineSize <= availablePositions;
                          
                          if (canFit) {
                            visibleOrders.push({ order, line, idx, orderHasDelivery });
                          } else {
                            hiddenCount.count++;
                          }
                        });
                      }
                    });
                    
                    return (
                      <>
                        {hiddenCount.count > 0 && (
                          <div className="hidden-items-warning">
                            ⚠️ {hiddenCount.count} order line{hiddenCount.count !== 1 ? 's' : ''} hidden due to limited space
                          </div>
                        )}
                        {visibleOrders.map(({ order, line, idx, orderHasDelivery }) => (
                          <div 
                            key={`${order.id}-${line.id || idx}`} 
                            className="order-item draggable"
                            draggable
                            onDragStart={(e) => handleDragStart(e, order, line)}
                          >
                            {orderHasDelivery && (
                              <span className="partial-badge" title="Partially allocated - some items already on truck">!</span>
                            )}
                            <div className="order-name">
                              {order.customer?.fullname || 'Unknown'}
                              {order.distance && (
                                <span className="order-distance" title="Distance from depot"> 📍 {order.distance} km</span>
                              )}
                            </div>
                            <div className="order-address">{order.delivery_address}</div>
                            <div className="order-product">
                              {line.product?.code || 'Unknown Product'} × {line.qty}
                              {line.size && ` (${line.size * line.qty} cube${(line.size * line.qty) !== 1 ? 's' : ''})`}
                              {line.weight_code && ` [${formatWeightCode(line.weight_code)}]`}
                            </div>
                            <div className="order-actions">
                              {(line.size * line.qty) >= 2 && (
                                <button 
                                  className="btn-split"
                                  onClick={() => handleOpenSplitModal(order, line)}
                                  title="Split this item into multiple whole cubic metre pieces"
                                >
                                  Split
                                </button>
                              )}
                              <span className="drag-hint">⋮⋮ drag to truck</span>
                            </div>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Middle content area - 5/12 column */}
          <div className="scheduling-content">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading delivery runs...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>{error}</p>
                <button className="btn btn-secondary" onClick={fetchDeliveryRuns}>
                  Retry
                </button>
              </div>
            ) : !selectedDate ? (
              <div className="empty-state">
                <p>Please select a date to view delivery runs</p>
              </div>
            ) : deliveryRuns.length === 0 ? (
              <div className="empty-state">
                <p>No delivery runs found for {selectedDate}</p>
                <button className="btn btn-primary" onClick={openCreateModal}>
                  Create First Run
                </button>
              </div>
            ) : selectedRun ? (
              <div className="run-details">
                <div className="details-header">
                </div>
                
                {/* Truck Graphic with Drop Zones */}
                <div className="truck-section">
                  <TruckGraphic 
                    onDropOrder={handleDropOrder}
                    onRemoveDelivery={handleRemoveDelivery}
                    onReorderDelivery={handleReorderDelivery}
                    onWigglePosition={setWigglePosition}
                    wigglePosition={wigglePosition}
                    disabled={!selectedRun || addingDelivery}
                    deliveries={selectedRun.deliveries || []}
                    canRemove={selectedRun?.status === 1}
                    showTrailerToggle={selectedRun?.status == 1}
                    onHideTrailer={handleHideTrailer}
                    trailerVisible={showTrailerVisible}
                    onToggleTrailer={handleToggleTrailer}
                    numberOfPositions={selectedRun?.number_of_positions || 13}
                  />
                  {addingDelivery && (
                    <div className="loading-overlay">
                      <div className="spinner-small"></div>
                      <span>Adding delivery...</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p>Select a delivery run to view details</p>
                <p className="hint">Or create a new run to get started</p>
              </div>
            )}
          </div>

          {/* Right panel - 6/12 column - Delivery Itinerary with Map (floated right) */}
          <div className="scheduling-itinerary">
            
            {selectedRun && (
              <div className="selected-run-info">
                <div className="run-code-row">
                  <h2>{selectedRun.name || selectedRun.code || '(Auto-generated)'}</h2>
                  {selectedRun.name && (
                    <span className="run-code-label">{selectedRun.code}</span>
                  )}
                  <span className={`status-badge ${getStatusClass(selectedRun.status)}`}>
                    {getStatusLabel(selectedRun.status)}
                  </span>
                </div>
                <div className="info-row">
                  <div className="time-display-row">
                    <span className="info-value">{formatStartTime(selectedRun.start_time)}</span>
                    {estimatedFinishTime && (
                      <span className="info-value"> → {formatStartTime(estimatedFinishTime)} (approx)</span>
                    )}
                    <button
                      className="btn btn-sm btn-link"
                      onClick={() => openEditRunModal(selectedRun)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="itinerary-with-map">
              
              <div className="itinerary-list">
                <h4>Delivery Itinerary</h4>
                {selectedRun ? (
                  <>
                    {selectedRun.deliveries && selectedRun.deliveries.length > 0 ? (
                      <div className="itinerary-deliveries">
                        {(() => {
                          // Group all delivery items by customer_order_id
                          const orderGroups = {};
                          selectedRun.deliveries.forEach((delivery) => {
                            const orderId = delivery.customer_order_id;
                            if (!orderGroups[orderId]) {
                              orderGroups[orderId] = {
                                customer: delivery.customer,
                                order: delivery.order,
                                orderId: orderId,
                                items: [],
                                maxPosition: 0
                              };
                            }
                            // Add all items from this delivery to the group
                            if (delivery.items) {
                              delivery.items.forEach((item) => {
                                const position = item.loading_position || 0;
                                orderGroups[orderId].items.push({
                                  ...item,
                                  deliveryId: delivery.id
                                });
                                if (position > orderGroups[orderId].maxPosition) {
                                  orderGroups[orderId].maxPosition = position;
                                }
                              });
                            }
                          });
                          
                          // Convert to array and sort by highest position (descending - highest first)
                          const sortedGroups = Object.values(orderGroups).sort((a, b) => {
                            return b.maxPosition - a.maxPosition;
                          });
                          
                          return sortedGroups.map((group, seqIdx) => (
                            <div key={group.orderId} className="itinerary-item">
                              <span className="itinerary-seq-number">
                                {seqIdx + 1}
                              </span>
                              
                              <div className="itinerary-details">
                                <span className="itinerary-customer ">
                                  {group.order.order_external_reference} for {group.customer?.fullname || 'Unknown'}
                                </span>
                                {routeLegs && routeLegs[seqIdx] && (
                                  <span className="itinerary-eta">
                                    ETA: {(() => {
                                      // Calculate cumulative travel time from start to this stop (in minutes)
                                      let cumulativeSeconds = 0;
                                      for (let i = 0; i <= seqIdx; i++) {
                                        cumulativeSeconds += parseInt(routeLegs[i].duration, 10) || 0;
                                      }
                                      // Add 15 min unload time for this stop
                                      const cumulativeMinutes = cumulativeSeconds + 15;
                                      return calculateETARange(selectedRun.start_time, cumulativeMinutes);
                                    })()}
                                  </span>
                                )}
                                <span className="itinerary-address">
                                  {group.order?.delivery_address || 'No address'}
                                </span>
                                {group.items.map((item, idx) => (
                                  
                                  <span key={idx} className="itinerary-product">
                                    {item.order_line?.product?.code || 'Product'} x {item.order_line?.qty || '0'}{item.order_line?.size ? ` (${item.order_line.size} cube)` : ''}

                                    <button 
                                      className="itinerary-position-badge fr"
                                      onClick={() => setWigglePosition(item.loading_position)}
                                      title="Click to highlight on truck"
                                    >
                                      #{item.loading_position}
                                    </button>
                                    
                                    
                                    
                                  </span>
                                ))}
                                {selectedRun.status === 1 && (
                                  <button
                                    className="btn btn-sm btn-danger remove-btn"
                                    onClick={() => handleRemoveDelivery(group.items[0]?.deliveryId, group.items[0]?.loading_position, group.items[0]?.id)}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    ) : (
                      <div className="empty-itinerary">
                        <p>No deliveries in itinerary</p>
                        <span className="hint">Add deliveries to see the route</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-itinerary">
                    <p>Select a delivery run</p>
                    <span className="hint">Choose a run to view its itinerary</span>
                  </div>
                )}
              </div>

              {selectedRun && (
                <div className="map-container">
                  <RouteMapView 
                    deliveries={selectedRun.deliveries || []}
                    startTime={selectedRun.start_time}
                    onEstimatedTimeChange={setEstimatedFinishTime}
                    onRouteLegsCalculated={setRouteLegs}
                  />
                  
                  <button 
                      className="btn btn-sm btn-secondary map-fullscreen-btn"
                      onClick={() => setShowFullscreenMap(true)}
                      title="View Fullscreen Map"
                    >
                      ⛶ Fullscreen
                    </button>

                  {selectedRun.status === 1 && (
                    <button 
                      className="btn btn-sm btn-confirm-run"
                      onClick={handleConfirmRun}
                      title="Confirm this delivery run"
                    >
                      ✓ Confirm Run
                    </button>
                  )}
                  {selectedRun.status === 2 && (
                    <button 
                      className="btn btn-sm btn-return-planning"
                      onClick={handleReturnToPlanning}
                      title="Return this run to Planning status"
                    >
                      ↩ Return to Planning
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Run Modal */}
      {showEditRunModal && selectedRun && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Delivery Run</h2>
              <button className="modal-close" onClick={closeEditRunModal}>&times;</button>
            </div>

            <form onSubmit={handleSaveRunEdit}>
              <div className="modal-body">
                {editRunError && (
                  <div className="error-message">{editRunError}</div>
                )}

                <div className="form-group">
                  <label htmlFor="edit-run-code">Code</label>
                  <div className="form-control readonly" id="edit-run-code">
                    {selectedRun.code}
                  </div>
                  <span className="hint">
                    The code identifies the run and does not change.
                  </span>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-run-name">Run Name (Optional)</label>
                  <input
                    type="text"
                    id="edit-run-name"
                    value={editRunName}
                    onChange={(e) => setEditRunName(e.target.value)}
                    className="form-control"
                    placeholder="e.g. Hamilton, Cambridge, Thames"
                  />
                  <span className="hint">
                    The destination area for this run. The same name can be used
                    many times over - one run a day to the same area is normal.
                  </span>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-run-time">Start Time</label>
                  <input
                    type="time"
                    id="edit-run-time"
                    value={editRunStartTime}
                    onChange={(e) => setEditRunStartTime(e.target.value)}
                    className="form-control"
                    required
                  />
                  <span className="hint">
                    {selectedRun.period_label
                      ? `Must stay in the ${selectedRun.period_label} period. This is a ${selectedRun.period_label} run, and the code and the vehicle count both depend on it.`
                      : 'The start time must stay in the same time period as the run.'}
                  </span>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeEditRunModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingRunEdit}
                >
                  {savingRunEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Run Modal - shared with the Delivery Diary */}
      <CreateRunModal
        isOpen={showCreateModal}
        onClose={closeCreateModal}
        onCreated={handleRunCreated}
        defaultDate={newRunDateFor(selectedDate)}
      />

      {/* Search Order Modal */}
      <SearchOrderModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        selectedDate={selectedDate}
        onReschedule={fetchScheduledOrders}
      />

      {/* Split Order Item Modal */}
      <SplitOrderItemModal
        isOpen={splitModalData.isOpen}
        onClose={handleCloseSplitModal}
        order={splitModalData.order}
        line={splitModalData.line}
        onSplitComplete={handleSplitComplete}
      />

      {/* Fullscreen Map Modal */}
      {showFullscreenMap && (
        <div className="modal-overlay" onClick={() => setShowFullscreenMap(false)}>
          <div className="modal modal-fullscreen-map" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Route Map - {selectedRun?.code || 'Delivery Run'}</h2>
              <button className="modal-close" onClick={() => setShowFullscreenMap(false)}>&times;</button>
            </div>
            <div className="modal-body modal-body-map">
              <RouteMapView 
                deliveries={selectedRun.deliveries || []} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanningPage;